import { SxProps, Theme, alpha } from '@mui/material';

const getCalendarColors = (theme: Theme) => ({
  background: {
    dark: theme.palette.mode === 'dark' ? '#212327' : '#f5f5f5',
    medium: theme.palette.mode === 'dark' ? '#4d5256' : '#e0e0e0',
    paper:
      theme.palette.mode === 'dark'
        ? 'rgb(48 51 57)'
        : theme.palette.background.paper,
  },
  event: {
    default: theme.palette.mode === 'dark' ? '#5FC8F1' : '#2196f3',
  },
  text: {
    primary: theme.palette.text.primary,
    secondary: theme.palette.text.secondary,
    dark: '#333',
  },
  borders: {
    default: theme.palette.mode === 'dark' ? 'rgb(102 109 114)' : '#d0d0d0',
    light:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.divider, 0.3)
        : alpha(theme.palette.divider, 0.5),
  },
  today: {
    background:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.08),
    text:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
  },
  header: {
    text: theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
  },
});

export const textEllipsis: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const smallIconStyle: SxProps<Theme> = {
  fontSize: '16px',
  mr: 1,
};

export const calendarContainer: SxProps<Theme> = {
  height: 'calc(100vh - 129px)',
  my: 2,
  mx: 0.5,
};

export const calendarPaper: SxProps<Theme> = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: 0,
  overflow: 'hidden',
  bgcolor: getCalendarColors(theme).background.paper,
  boxShadow:
    theme.palette.mode === 'dark' ? theme.shadows[2] : theme.shadows[1],
});

export const calendarContent: SxProps<Theme> = {
  flexGrow: 1,
  px: 3,
  pb: 3,
};

export const getFullCalendarGlobalStyles = (theme: Theme) => {
  const colors = getCalendarColors(theme);

  return {
    '.fc .fc-toolbar.fc-header-toolbar': {
      marginBottom: 0,
    },
    '.fc .fc-timegrid-slot-minor': {
      borderTopStyle: 'none',
    },
    '.fc .fc-timegrid-slot-label': {
      border: 0,
    },
    '.fc tr': {
      height: '31px',
    },
    '.fc-theme-standard .fc-popover': {
      zIndex: 1000,
      border: 'none',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.5)',
    },
    '.fc-theme-standard .fc-popover-header': {
      backgroundColor:
        theme.palette.mode === 'dark' ? '#4d5256' : theme.palette.grey[200],
      color: theme.palette.text.primary,
    },
    '.fc .fc-more-popover .fc-popover-body': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgb(48 51 57)'
          : theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    '.fc-theme-standard td, .fc-theme-standard th, .fc-theme-standard .fc-scrollgrid':
      {
        border: `1px solid ${colors.borders.default}`,
      },
    '.fc-day-today.fc-col-header-cell': {
      backgroundColor: colors.today.background,
      '.fc-col-header-cell-cushion': {
        color: colors.today.text,
      },
    },
    '.fc .fc-col-header-cell': {
      height: '60px',
      verticalAlign: 'middle',
    },
    '.fc-col-header-cell-cushion, .fc-daygrid-day-number': {
      color: colors.header.text,
      textDecoration: 'none',
    },
    '.fc-scroller.fc-scroller-liquid-absolute': {
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    '.fc-event': {
      border: 'none',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: 'var(--event-color)',
        filter: 'saturate(0.8) brightness(1.2)',
      },
      '&:hover': {
        filter:
          theme.palette.mode === 'dark'
            ? 'brightness(1.2)'
            : 'brightness(0.95)',
      },
    },
    '.fc-timegrid-event-harness-inset .fc-timegrid-event, .fc-timegrid-event.fc-event-mirror, .fc-timegrid-more-link':
      {
        boxShadow: 'none',
      },
    '.fc-theme-standard': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? colors.background.paper
          : theme.palette.background.default,
    },
    '.fc-day-sat, .fc-day-sun': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.action.hover, 0.02)
          : alpha(theme.palette.action.hover, 0.03),
    },
    '.fc-timegrid-slot-label-cushion': {
      color: theme.palette.text.secondary,
    },
    '.fc-timegrid-axis-cushion': {
      color: theme.palette.text.secondary,
    },
    '.fc-daygrid-day-frame': {
      cursor: 'pointer',
    },
    '.fc-timegrid-slot': {
      cursor: 'pointer',
    },
    '.fc-timegrid-slot-lane': {
      cursor: 'pointer',
    },
    '.fc-daygrid-day': {
      cursor: 'pointer',
    },
  };
};

export const eventFormStyles = {
  divider: (theme: Theme): SxProps<Theme> => ({
    border: `1px solid ${theme.palette.divider}`,
  }),
  titleField: {
    flexGrow: 1,
    flexBasis: '66%',
  } as SxProps<Theme>,
  colorField: {
    flexShrink: 1,
    flexBasis: '34%',
  } as SxProps<Theme>,
  submitSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    pb: 2,
  } as SxProps<Theme>,
  sectionTitle: {
    mt: 2,
  } as SxProps<Theme>,
  sectionStack: {
    mt: 2,
  } as SxProps<Theme>,
  submitButton: {
    mt: 2,
  } as SxProps<Theme>,
};

export const calendarHeaderStyles = {
  container: {
    justifyContent: 'space-between',
    alignItems: 'center',
    px: 3,
    py: 2,
  } as SxProps<Theme>,
  titleBox: {
    width: 210,
  } as SxProps<Theme>,
  titleText: {
    fontWeight: 500,
    minWidth: 155,
  } as SxProps<Theme>,
  navigationButton: {
    borderRadius: 0,
  } as SxProps<Theme>,
  viewSelect: {
    maxHeight: 40,
    width: 140,
    borderRadius: 0,
  } as SxProps<Theme>,
  addButton: {
    height: 40,
    width: 140,
    borderRadius: 0,
  } as SxProps<Theme>,
};

export const eventEditorStyles = {
  drawerPaper: (theme: Theme): SxProps<Theme> => ({
    bgcolor:
      theme.palette.mode === 'dark'
        ? '#212327'
        : theme.palette.background.default,
  }),
  contentStack: {
    width: 700,
  } as SxProps<Theme>,
  header: (theme: Theme): SxProps<Theme> => ({
    backgroundColor:
      theme.palette.mode === 'dark' ? '#4d5256' : theme.palette.grey[300],
    borderRadius: 0,
  }),
  titleTypography: {
    p: 2,
  } as SxProps<Theme>,
};

export const eventPopoverStyles = {
  card: {
    width: 375,
    p: 0,
  } as SxProps<Theme>,
  cardHeader: (theme: Theme): SxProps<Theme> => ({
    p: 1,
    bgcolor:
      theme.palette.mode === 'dark' ? '#4d5256' : theme.palette.grey[200],
  }),
  cardContent: (theme: Theme): SxProps<Theme> => ({
    p: 0,
    border: `1px solid ${theme.palette.mode === 'dark' ? '#4d5256' : theme.palette.divider}`,
  }),
  cardSection: {
    p: 1,
    display: 'flex',
    alignItems: 'center',
  } as SxProps<Theme>,
  icon: smallIconStyle,
  stackNameBox: {
    flex: 1,
    textTransform: 'capitalize',
  } as SxProps<Theme>,
  commandBox: {
    flex: 1,
    ...textEllipsis,
  } as SxProps<Theme>,
  divider: (theme: Theme): SxProps<Theme> => ({
    my: 1,
    border: `1px solid ${theme.palette.divider}`,
  }),
};

export const eventTileStyles = {
  container: (theme: Theme) => ({
    color: getCalendarColors(theme).text.dark,
    fontSize: '0.85rem',
    fontWeight: 500,
    flex: 1,
    paddingLeft: '6px',
    paddingTop: '3px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden',
  }),
  title: {
    fontWeight: 500,
  } as const,
  time: {
    fontSize: '0.75rem',
    opacity: 0.9,
    fontWeight: 400,
  } as const,
  longEventTime: {
    ...textEllipsis,
    fontSize: '0.75rem',
    opacity: 0.9,
    fontWeight: 400,
  } as const,
};
